import React, { Dispatch, Fragment, ReactElement, SetStateAction, useMemo, useState } from 'react';
import { Button }from '@cmsgov/design-system';

import { FormattedMessage } from 'react-intl';

// Components

import Form from '@lib/wrappers/Form';
import ModalHeader from '@components/common/headers/ModalHeader';
import UniversalInput from '@lib/wrappers/UniversalInput';

// Utils

import { normalizeNameOfPerson } from '@utils/normalize/normalizeNameOfPerson';
import { parseFieldChangeArgs } from '@utils/ssap/parseFieldChangeArgs';

// Constants

import { DROPDOWN_OPTIONS } from '@constants/ssap/hhmTypes/dropdownOptions';
import { INPUT_TYPES } from '@constants/inputs/inputTypes';
import { VALIDATION_TYPES } from '@constants/inputs/validationTypes';

// Types

import { CommonModalProps, OnChangeArgTypes, PersonName } from '@components/ssap/SsapTypes';

interface IDeceasedMemberModal extends CommonModalProps {
    modal: {
        modalData: {
            actions: {
                handleMemberDeletion: Function
            },
            coverageYear: number,
            personName: PersonName
        }
    },
    modalHeaderProps: {
        modalId
    }
}


const DeceasedMemberModal = (props: IDeceasedMemberModal): ReactElement | null => {

    const {
        actions: {
            hideModal
        },
        bottomAnchors: [
            anchor_1,
        ],
        boundAnchor,
        formName = '',
        modal: {
            modalData: {
                actions: {
                    handleMemberDeletion
                },
                coverageYear,
                personName
            }
        },
        modalHeaderProps,
        modalHeaderProps: {
            modalId
        }
    } = props;

    interface LocalState {
        removeAction: boolean | null
    }

    const INITIAL_STATE: LocalState = {
        removeAction: false
    };

    const [
        localState,
        updateLocalState
    ]: [
        LocalState,
        Dispatch<SetStateAction<LocalState>>
    ] = useState<LocalState>(INITIAL_STATE);

    const {
        removeAction
    }: LocalState = localState;

    const deceasedEarlierMemberName: string = useMemo(
        (): string => normalizeNameOfPerson(personName),
        [ personName ]
    );

    const onFieldChange = (...args: OnChangeArgTypes): void => {
        const { name, value } = parseFieldChangeArgs(args);

        updateLocalState((state: LocalState): LocalState => ({
            ...state,
            [ name ]: value
        }));
    };

    const handleHideClick = (): void => hideModal();

    const handleSaveClick = (): void => {
        if(!removeAction) {
            handleMemberDeletion();
        }
        hideModal();
    };

    return (
        <Fragment>
            <div className = 'gi-modal__header__head' >
                <ModalHeader { ...modalHeaderProps } />
            </div>

            <div id = 'deceasedMemberModal' className = 'gi-modal__body'>
                <div className = 'usa-alert gi-alert gi-alert__left-accent gi-alert__info' >
                    <div className = 'gi-alert__body' >
                        <p className = 'gi-alert__text' data-testid={`${modalId}_body_alert`}>
                            <FormattedMessage
                                id = 'modals.deceased_member.death_date_before_coverage_year'
                                values = { {
                                    coverageYear
                                } }
                            />
                        </p>
                    </div>
                </div>

                <Form { ...{ formName } } >
                    <UniversalInput
                        legend = { {
                            legendText: 'ssap.input.legend.deceased_member_options',
                            legendTextReplacements: {
                                personName: <strong>{ deceasedEarlierMemberName }</strong>
                            }
                        } }
                        label = { {
                            showRequiredIcon: true,
                            ulClass: [ 'margin-b-20' ]
                        } }
                        inputData = { {
                            isInputRequired: true,
                            type: INPUT_TYPES.RADIO_BUTTONS,
                            liClass: [ 'text-left' ],
                            name: 'removeAction',
                            value: removeAction,
                            options: DROPDOWN_OPTIONS.DECEASED_MEMBER_ACTIONS,
                            optionsReplacements: {
                                personName: deceasedEarlierMemberName,
                            },
                            validationType: VALIDATION_TYPES.CHOSEN,
                            currentData: localState,
                            fields: [ ]
                        } }
                        errorClasses={ [ 'margin-l-0' ] }
                        inputActions={ {
                            onFieldChange
                        } }
                    />
                </Form>
            </div>

            <div className = 'gi-modal__footer' >
                <div className = 'grid-row controls' >
                    <div className = 'grid-offset-4 txt-left' >
                        <Button
                            className = 'usa-button usa-button--outline gi-button gi-button__outline'
                            data-testid={`${modalId}-cancel_btn`}
                            onClick = { handleHideClick }
                        >
                            <FormattedMessage id = 'button.cancel' />
                        </Button>
                        <Button
                            className = 'usa-button gi-button gi-button__primary'
                            data-testid={`${modalId}-save_btn`}
                            onClick = { handleSaveClick }
                            inputRef = { boundAnchor(anchor_1) }
                        >
                            <FormattedMessage id = 'button.save_continue' />
                        </Button>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default DeceasedMemberModal;